import * as Sentry from "@sentry/remix";
import dayjs from "dayjs";
import "dayjs/locale/fa";
import relativeTime from "dayjs/plugin/relativeTime";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import i18next from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import jalaliday from "jalaliday";
import { StrictMode, startTransition, useEffect } from "react";
import { hydrateRoot } from "react-dom/client";
import { I18nextProvider, initReactI18next } from "react-i18next";
import { useLocation, useMatches } from "react-router";
import { HydratedRouter } from "react-router/dom";
import { getInitialNamespaces } from "remix-i18next/client";
import renderDigits from "./helpers/renderDigits";
import * as i18n from "./i18n";

// Configure dayjs plugins
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);
dayjs.extend(jalaliday);

// Sentry.init({
//   dsn: "https://9b949f3ea1672baa4332b1ee2d6d4730@o4507438934654976.ingest.de.sentry.io/4507438940291152",
//   integrations: [
//     Sentry.browserTracingIntegration({
//       useEffect,
//       useLocation,
//       useMatches,
//     }),
//     // Replay is only available in the client
//     Sentry.replayIntegration(),
//   ],

//   // Set tracesSampleRate to 1.0 to capture 100%
//   // of transactions for performance monitoring.
//   // We recommend adjusting this value in production
//   tracesSampleRate: 1.0, // Capture 100% of the transactions, reduce in production!

//   // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled

//   // Capture Replay for 10% of all sessions,
//   // plus for 100% of sessions with an error
//   replaysSessionSampleRate: 0.1,
//   replaysOnErrorSampleRate: 1.0,
//   enabled: process.env.NODE_ENV !== "development",
// });

async function main() {
  await i18next
    .use(initReactI18next) // Tell i18next to use the react-i18next plugin
    .use(LanguageDetector) // Setup a client-side language detector
    .init({
      ...i18n,
      ns: getInitialNamespaces(),
      detection: {
        // Here only enable htmlTag detection, we'll detect the language only
        // server-side with remix-i18next, by using the `<html lang>` attribute
        // we can communicate to the client the language detected server-side
        order: ["htmlTag"],
        // Because we only use htmlTag, there's no reason to cache the language
        // on the browser, so we disable it
        caches: [],
      },
    });

  // Add formatters after i18next initialization
  if (i18next.services && i18next.services.formatter) {
    i18next.services.formatter.add("toCurrency", (value, lng, options) => {
      if (!value) return "";
      const { currency } = options;
      return String(value * currency.rate);
    });

    i18next.services.formatter.add("renderDigits", (value, lng, options) => {
      if (!value) return "";
      return renderDigits({ data: value, i18n: i18next, options: { maximumFractionDigits: options.currency.code === "IRT" ? 0 : 3, toLocaleString: true } });
    });

    i18next.services.formatter.add("dayjs", (value, lng, options) => {
      if (!value) return "";
      if (!dayjs(value).isValid()) return value;
      return dayjs(value).tz("Asia/Tehran").format(options.format);
    });

    i18next.services.formatter.add("toUnit", (value, lng, options) => {
      if (!value) return "";
      return i18next.t(`units.${options.item.unit}`);
    });
  }

  startTransition(() => {
    hydrateRoot(
      document,
      <I18nextProvider i18n={i18next}>
        <StrictMode>
          <HydratedRouter />
        </StrictMode>
      </I18nextProvider>,
    );
  });
}

main().catch((error) => console.error(error));
